import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    FaArrowLeft,
    FaArrowRight,
    FaBookOpen,
    FaCheckCircle,
    FaClock,
    FaExclamationTriangle,
    FaGraduationCap,
    FaLayerGroup,
    FaPlay,
    FaSpinner,
    FaUserTie,
    FaUsers,
    FaVideo,
} from "react-icons/fa";

import api from "../api/api";

/*
 * KEEP YOUR EXISTING courseStyles TEMPLATE LITERAL HERE.
 *
 * Example:
 *
 * const courseStyles = `
 *     ... your existing CAYEP course CSS ...
 * `;
 */

const Card = () => {
    const sectionRef = useRef(null);

    const COURSES_PER_PAGE = 6;

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const getMediaUrl = useCallback((value) => {
        if (!value) {
            return "";
        }

        const url = String(value).trim();

        if (!url) {
            return "";
        }

        if (
            url.startsWith("http://") ||
            url.startsWith("https://") ||
            url.startsWith("blob:") ||
            url.startsWith("data:")
        ) {
            return url;
        }

        const baseURL =
            api?.defaults?.baseURL ||
            window.location.origin;

        try {
            const base = new URL(
                baseURL,
                window.location.origin
            );

            return new URL(
                url,
                `${base.protocol}//${base.host}`
            ).toString();
        } catch {
            try {
                return new URL(
                    url,
                    window.location.origin
                ).toString();
            } catch {
                return url;
            }
        }
    }, []);

    const normalizeCourse = useCallback(
        (course) => {
            if (!course) {
                return null;
            }

            const thumbnail =
                course.thumbnail ||
                course.image ||
                "";

            const video =
                course.video_url ||
                course.video ||
                "";

            return {
                ...course,

                id: course.id,

                course_title:
                    course.course_title ||
                    course.name ||
                    "Untitled Course",

                name:
                    course.name ||
                    course.course_title ||
                    "Untitled Course",

                description:
                    course.description ||
                    "Professional training course designed to develop practical knowledge, leadership skills, and workplace excellence.",

                category:
                    course.category ||
                    "Leadership Training",

                duration:
                    course.duration ||
                    "",

                lessons:
                    course.lessons ||
                    "",

                level:
                    course.level ||
                    "",

                facilitator:
                    course.facilitator ||
                    course.facilitator_name ||
                    "",

                facilitator_name:
                    course.facilitator_name ||
                    course.facilitator ||
                    "",

                thumbnail:
                    getMediaUrl(thumbnail),

                image:
                    getMediaUrl(thumbnail),

                video_url:
                    getMediaUrl(video),

                video:
                    getMediaUrl(video),

                price:
                    course.price ?? "",

                old_price:
                    course.old_price ??
                    course.oldPrice ??
                    "",

                current_price:
                    course.current_price ??
                    course.price ??
                    "",

                slots:
                    course.slots ?? 0,

                slots_taken:
                    course.slots_taken ?? 0,

                slots_remaining:
                    course.slots_remaining,

                is_full:
                    course.is_full === true,

                slot_status:
                    course.slot_status || "",

                latest_drop_at:
                    course.latest_drop_at ||
                    null,

                latest_drop_ago:
                    course.latest_drop_ago ||
                    "",
            };
        },
        [getMediaUrl]
    );

    /*
     * Loads every DRF pagination page.
     *
     * Backend may return:
     *
     * page 1 -> 10 courses -> next page
     * page 2 -> 10 courses -> next page
     * page 3 -> remaining courses -> next null
     *
     * React then has the complete catalog and handles
     * the visible 6-card pagination locally.
     */
    const fetchCourses = useCallback(
        async (showLoading = true) => {
            try {
                if (showLoading) {
                    setLoading(true);
                }

                setError("");

                const allCourses = [];
                const visitedUrls = new Set();

                let nextUrl =
                    "api/users/subjects/";

                while (nextUrl) {
                    const requestUrl = String(
                        nextUrl
                    ).trim();

                    if (!requestUrl) {
                        break;
                    }

                    if (
                        visitedUrls.has(
                            requestUrl
                        )
                    ) {
                        console.warn(
                            "Course pagination stopped because the API returned a repeated pagination URL:",
                            requestUrl
                        );

                        break;
                    }

                    visitedUrls.add(
                        requestUrl
                    );

                    const response =
                        await api.get(
                            requestUrl
                        );

                    const responseData =
                        response?.data;

                    let pageCourses = [];

                    if (
                        Array.isArray(
                            responseData
                        )
                    ) {
                        pageCourses =
                            responseData;
                    } else if (
                        Array.isArray(
                            responseData?.results
                        )
                    ) {
                        pageCourses =
                            responseData.results;
                    } else if (
                        Array.isArray(
                            responseData?.data
                        )
                    ) {
                        pageCourses =
                            responseData.data;
                    } else if (
                        Array.isArray(
                            responseData?.courses
                        )
                    ) {
                        pageCourses =
                            responseData.courses;
                    }

                    allCourses.push(
                        ...pageCourses
                    );

                    if (
                        responseData &&
                        !Array.isArray(
                            responseData
                        )
                    ) {
                        nextUrl =
                            responseData.next ||
                            null;
                    } else {
                        nextUrl = null;
                    }
                }

                /*
                 * Remove accidental duplicate courses
                 * returned by the backend while preserving
                 * their original order.
                 */
                const uniqueCourses = [];
                const courseIds = new Set();

                allCourses.forEach(
                    (course, index) => {
                        const normalized =
                            normalizeCourse(
                                course
                            );

                        if (!normalized) {
                            return;
                        }

                        const identity =
                            normalized.id !==
                                null &&
                            normalized.id !==
                                undefined
                                ? `id-${normalized.id}`
                                : `index-${index}`;

                        if (
                            courseIds.has(
                                identity
                            )
                        ) {
                            return;
                        }

                        courseIds.add(
                            identity
                        );

                        uniqueCourses.push(
                            normalized
                        );
                    }
                );

                setCourses(
                    uniqueCourses
                );

                setSelectedCourse(
                    (current) => {
                        if (!current) {
                            return null;
                        }

                        const refreshed =
                            uniqueCourses.find(
                                (course) =>
                                    String(
                                        course.id
                                    ) ===
                                    String(
                                        current.id
                                    )
                            );

                        return (
                            refreshed ||
                            current
                        );
                    }
                );

                /*
                 * If a refresh happens while the user is
                 * viewing a later page and the catalog has
                 * become smaller, move them to the last
                 * valid page.
                 */
                setCurrentPage(
                    (page) => {
                        const calculatedPages =
                            Math.max(
                                1,
                                Math.ceil(
                                    uniqueCourses.length /
                                        COURSES_PER_PAGE
                                )
                            );

                        return Math.min(
                            Math.max(
                                page,
                                1
                            ),
                            calculatedPages
                        );
                    }
                );
            } catch (requestError) {
                console.error(
                    "Unable to load courses:",
                    requestError
                );

                if (showLoading) {
                    setError(
                        requestError
                            ?.response?.data
                            ?.detail ||
                            requestError
                                ?.response
                                ?.data
                                ?.message ||
                            "Unable to load training courses at this time."
                    );
                }
            } finally {
                if (showLoading) {
                    setLoading(false);
                }
            }
        },
        [normalizeCourse]
    );

    useEffect(() => {
        fetchCourses(true);
    }, [fetchCourses]);

    useEffect(() => {
        const refreshInterval =
            window.setInterval(() => {
                fetchCourses(false);
            }, 60000);

        return () => {
            window.clearInterval(
                refreshInterval
            );
        };
    }, [fetchCourses]);

    const totalPages = Math.max(
        1,
        Math.ceil(
            courses.length /
                COURSES_PER_PAGE
        )
    );

    useEffect(() => {
        setCurrentPage(
            (page) =>
                Math.min(
                    Math.max(page, 1),
                    totalPages
                )
        );
    }, [totalPages]);

    const paginatedCourses = useMemo(() => {
        const startIndex =
            (currentPage - 1) *
            COURSES_PER_PAGE;

        return courses.slice(
            startIndex,
            startIndex +
                COURSES_PER_PAGE
        );
    }, [
        courses,
        currentPage,
    ]);

    const paginationStart =
        courses.length === 0
            ? 0
            : (currentPage - 1) *
                  COURSES_PER_PAGE +
              1;

    const paginationEnd =
        courses.length === 0
            ? 0
            : Math.min(
                  currentPage *
                      COURSES_PER_PAGE,
                  courses.length
              );

    /*
     * Pagination is only actually usable when there
     * are more than 6 courses.
     */
    const hasPagination =
        courses.length >
        COURSES_PER_PAGE;

    const canGoPrevious =
        hasPagination &&
        currentPage > 1 &&
        !loading;

    const canGoNext =
        hasPagination &&
        currentPage < totalPages &&
        !loading;

    const changePage = useCallback(
        (page) => {
            if (loading) {
                return;
            }

            const safePage =
                Math.min(
                    Math.max(
                        page,
                        1
                    ),
                    totalPages
                );

            if (
                safePage ===
                currentPage
            ) {
                return;
            }

            setCurrentPage(
                safePage
            );

            window.setTimeout(
                () => {
                    document
                        .getElementById(
                            "cayep-course-catalog"
                        )
                        ?.scrollIntoView({
                            behavior:
                                "smooth",
                            block:
                                "start",
                        });
                },
                50
            );
        },
        [
            currentPage,
            totalPages,
            loading,
        ]
    );

    const handlePrevious =
        useCallback(() => {
            if (
                !canGoPrevious
            ) {
                return;
            }

            changePage(
                currentPage - 1
            );
        }, [
            canGoPrevious,
            currentPage,
            changePage,
        ]);

    const handleNext =
        useCallback(() => {
            if (!canGoNext) {
                return;
            }

            changePage(
                currentPage + 1
            );
        }, [
            canGoNext,
            currentPage,
            changePage,
        ]);

    useEffect(() => {
        if (!sectionRef.current) {
            return undefined;
        }

        const section =
            sectionRef.current;

        const handleMouseMove =
            (event) => {
                const rect =
                    section.getBoundingClientRect();

                if (
                    !rect.width ||
                    !rect.height
                ) {
                    return;
                }

                const x =
                    ((event.clientX -
                        rect.left) /
                        rect.width) *
                    100;

                const y =
                    ((event.clientY -
                        rect.top) /
                        rect.height) *
                    100;

                section.style.setProperty(
                    "--mouse-x",
                    `${x}%`
                );

                section.style.setProperty(
                    "--mouse-y",
                    `${y}%`
                );
            };

        section.addEventListener(
            "mousemove",
            handleMouseMove
        );

        return () => {
            section.removeEventListener(
                "mousemove",
                handleMouseMove
            );
        };
    }, []);

    const particles = useMemo(
        () =>
            Array.from(
                {
                    length: 28,
                },
                (_, index) => ({
                    id: index,
                    x:
                        3 +
                        ((index * 17) %
                            94),
                    y:
                        5 +
                        ((index * 31) %
                            90),
                    delay:
                        (index % 9) *
                        0.45,
                })
            ),
        []
    );

    const formatMoney =
        useCallback(
            (value) => {
                if (
                    value ===
                        null ||
                    value ===
                        undefined ||
                    value === ""
                ) {
                    return "0";
                }

                const numericValue =
                    Number(
                        String(value)
                            .replace(
                                /,/g,
                                ""
                            )
                            .replace(
                                /[^\d.-]/g,
                                ""
                            )
                    );

                if (
                    !Number.isFinite(
                        numericValue
                    )
                ) {
                    return String(
                        value
                    );
                }

                return new Intl.NumberFormat(
                    "en-US",
                    {
                        maximumFractionDigits: 2,
                    }
                ).format(
                    numericValue
                );
            },
            []
        );

    const getSlotState =
        useCallback(
            (course) => {
                const rawTotal =
                    Number(
                        course?.slots ??
                            0
                    );

                const rawTaken =
                    Number(
                        course?.slots_taken ??
                            0
                    );

                const total =
                    Number.isFinite(
                        rawTotal
                    )
                        ? Math.max(
                              0,
                              rawTotal
                          )
                        : 0;

                const taken =
                    Number.isFinite(
                        rawTaken
                    )
                        ? Math.max(
                              0,
                              rawTaken
                          )
                        : 0;

                const backendStatus =
                    String(
                        course?.slot_status ||
                            ""
                    ).toLowerCase();

                const unlimited =
                    backendStatus ===
                        "unlimited" ||
                    (!backendStatus &&
                        total === 0 &&
                        course?.is_full !==
                            true);

                const parsedRemaining =
                    Number(
                        course?.slots_remaining
                    );

                const calculatedRemaining =
                    Math.max(
                        total -
                            taken,
                        0
                    );

                const remaining =
                    unlimited
                        ? null
                        : Number.isFinite(
                              parsedRemaining
                          )
                        ? Math.max(
                              0,
                              parsedRemaining
                          )
                        : calculatedRemaining;

                const full =
                    backendStatus ===
                        "full" ||
                    course?.is_full ===
                        true ||
                    (!unlimited &&
                        remaining <=
                            0);

                const lastSlot =
                    !full &&
                    (backendStatus ===
                        "last_slot" ||
                        (!unlimited &&
                            remaining ===
                                1));

                return {
                    total,
                    taken,
                    remaining,
                    full,
                    lastSlot,
                    unlimited,
                };
            },
            []
        );

    const getSlotLabel =
        useCallback(
            (course) => {
                const slotState =
                    getSlotState(
                        course
                    );

                if (
                    slotState.full
                ) {
                    return "Class Full";
                }

                if (
                    slotState.unlimited
                ) {
                    return "Open";
                }

                if (
                    slotState.lastSlot
                ) {
                    return "Last Slot";
                }

                return `${slotState.remaining} Available`;
            },
            [getSlotState]
        );

    const handleOpenCourse =
        useCallback(
            (course) => {
                setVideoLoading(
                    Boolean(
                        course?.video_url
                    )
                );

                setSelectedCourse(
                    course
                );

                window.setTimeout(
                    () => {
                        document
                            .getElementById(
                                "cayep-course-learning"
                            )
                            ?.scrollIntoView({
                                behavior:
                                    "smooth",
                                block:
                                    "start",
                            });
                    },
                    50
                );
            },
            []
        );

    const handleBackToCourses =
        useCallback(() => {
            setSelectedCourse(
                null
            );

            setVideoLoading(
                false
            );

            window.setTimeout(
                () => {
                    document
                        .getElementById(
                            "cayep-course-catalog"
                        )
                        ?.scrollIntoView({
                            behavior:
                                "smooth",
                            block:
                                "start",
                        });
                },
                50
            );
        }, []);

    const renderCourseCard = (
        course,
        index
    ) => {
        const slotState =
            getSlotState(course);

        const imageUrl =
            course.thumbnail ||
            course.image ||
            "";

        const hasVideo =
            Boolean(
                course.video_url
            );

        const currentPrice =
            course.current_price ??
            course.price;

        const oldPrice =
            course.old_price;

        return (
            <article
                className="cayep-course-card"
                key={
                    course.id ||
                    `course-${index}`
                }
                onClick={() =>
                    handleOpenCourse(
                        course
                    )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(
                    event
                ) => {
                    if (
                        event.key ===
                            "Enter" ||
                        event.key ===
                            " "
                    ) {
                        event.preventDefault();

                        handleOpenCourse(
                            course
                        );
                    }
                }}
            >
                <div className="cayep-course-image">
                    {imageUrl ? (
                        <img
                            src={
                                imageUrl
                            }
                            alt={
                                course.course_title
                            }
                            onError={(
                                event
                            ) => {
                                event.currentTarget.style.display =
                                    "none";
                            }}
                        />
                    ) : (
                        <div className="cayep-course-no-image">
                            <FaBookOpen />

                            <span>
                                CAYEP TRAINING
                            </span>
                        </div>
                    )}

                    <div className="cayep-course-image-overlay" />

                    <div className="cayep-course-number">
                        {String(
                            index + 1
                        ).padStart(
                            2,
                            "0"
                        )}
                    </div>

                    <div className="cayep-course-category">
                        {
                            course.category
                        }
                    </div>

                    <button
                        type="button"
                        className="cayep-course-play"
                        onClick={(
                            event
                        ) => {
                            event.stopPropagation();

                            handleOpenCourse(
                                course
                            );
                        }}
                        aria-label={`Open ${course.course_title}`}
                    >
                        <FaPlay />
                    </button>
                </div>

                <div className="cayep-course-content">
                    <h4>
                        {
                            course.course_title
                        }
                    </h4>

                    <p className="cayep-course-description">
                        {
                            course.description
                        }
                    </p>

                    <div className="cayep-course-meta">
                        {course.facilitator && (
                            <span>
                                <FaUserTie />

                                {
                                    course.facilitator
                                }
                            </span>
                        )}

                        {course.level && (
                            <span>
                                <FaGraduationCap />

                                {
                                    course.level
                                }
                            </span>
                        )}

                        {course.duration && (
                            <span>
                                <FaClock />

                                {
                                    course.duration
                                }
                            </span>
                        )}

                        {course.lessons && (
                            <span>
                                <FaLayerGroup />

                                {
                                    course.lessons
                                }
                            </span>
                        )}

                        {hasVideo && (
                            <span>
                                <FaVideo />

                                Video learning
                            </span>
                        )}
                    </div>

                    <div className="cayep-course-capacity">
                        <div className="cayep-capacity-main">
                            {slotState.full ? (
                                <FaExclamationTriangle />
                            ) : (
                                <FaUsers />
                            )}

                            <div>
                                <strong>
                                    {slotState.full
                                        ? "Course Capacity"
                                        : slotState.unlimited
                                        ? "Course Capacity"
                                        : `${slotState.remaining} ${
                                              slotState.remaining ===
                                              1
                                                  ? "slot"
                                                  : "slots"
                                          } available`}
                                </strong>

                                <span>
                                    {slotState.unlimited
                                        ? "Open enrollment"
                                        : `${slotState.taken} of ${slotState.total} ${
                                              slotState.total ===
                                              1
                                                  ? "seat"
                                                  : "seats"
                                          } occupied`}
                                </span>

                                {course.latest_drop_ago && (
                                    <small>
                                        <FaClock />

                                        Seat released{" "}
                                        {
                                            course.latest_drop_ago
                                        }
                                    </small>
                                )}
                            </div>
                        </div>

                        <span
                            className={`cayep-slot-badge ${
                                slotState.full
                                    ? "full"
                                    : slotState.lastSlot
                                    ? "last"
                                    : ""
                            }`}
                        >
                            {getSlotLabel(
                                course
                            )}
                        </span>
                    </div>

                    <div className="cayep-course-divider" />

                    <div className="cayep-course-bottom">
                        <div className="cayep-course-price">
                            <strong>
                                {currentPrice !==
                                    null &&
                                currentPrice !==
                                    undefined &&
                                currentPrice !==
                                    ""
                                    ? `$${formatMoney(
                                          currentPrice
                                      )} USD`
                                    : "FREE"}
                            </strong>

                            {oldPrice &&
                                Number(
                                    String(
                                        oldPrice
                                    ).replace(
                                        /,/g,
                                        ""
                                    )
                                ) >
                                    Number(
                                        String(
                                            currentPrice ||
                                                0
                                        ).replace(
                                            /,/g,
                                            ""
                                        )
                                    ) && (
                                    <del>
                                        ${" "}
                                        {formatMoney(
                                            oldPrice
                                        )} USD
                                    </del>
                                )}
                        </div>

                        <button
                            type="button"
                            className="cayep-course-button"
                            onClick={(
                                event
                            ) => {
                                event.stopPropagation();

                                handleOpenCourse(
                                    course
                                );
                            }}
                        >
                            <span>
                                View Course
                            </span>

                            <FaArrowRight />
                        </button>
                    </div>
                </div>
            </article>
        );
    };

    if (selectedCourse) {
        const selectedImage =
            selectedCourse.thumbnail ||
            selectedCourse.image ||
            "";

        const selectedVideo =
            selectedCourse.video_url ||
            selectedCourse.video ||
            "";

        return (
            <>
                <style>
                    {courseStyles}
                </style>

                <section
                    className="cayep-courses-section cayep-learning-page"
                    ref={
                        sectionRef
                    }
                    id="cayep-course-learning"
                >
                    <div className="cayep-courses-background">
                        <div className="cayep-grid" />

                        <div className="cayep-energy cayep-energy-one" />
                        <div className="cayep-energy cayep-energy-two" />
                        <div className="cayep-energy cayep-energy-three" />

                        <div className="cayep-orb cayep-orb-one" />
                        <div className="cayep-orb cayep-orb-two" />

                        <div className="cayep-particles">
                            {particles.map(
                                (
                                    particle
                                ) => (
                                    <span
                                        key={
                                            particle.id
                                        }
                                        style={{
                                            "--particle-x": `${particle.x}%`,
                                            "--particle-y": `${particle.y}%`,
                                            "--particle-delay": `${particle.delay}s`,
                                        }}
                                    />
                                )
                            )}
                        </div>
                    </div>

                    <div className="cayep-courses-container">
                        <div className="cayep-course-learning-top">
                            <button
                                type="button"
                                className="cayep-back-courses-button"
                                onClick={
                                    handleBackToCourses
                                }
                            >
                                <FaArrowLeft />

                                <span>
                                    Back to Courses
                                </span>
                            </button>

                            <div className="cayep-learning-status">
                                <span className="cayep-status-dot" />

                                COURSE LEARNING ENVIRONMENT
                            </div>
                        </div>

                        <div className="cayep-course-learning-header">
                            <div>
                                <div className="cayep-section-label">
                                    <span className="cayep-label-line" />

                                    CAYEP • LEADERSHIP TRAINING
                                </div>

                                <h1>
                                    {
                                        selectedCourse.course_title
                                    }
                                </h1>

                                <p>
                                    {
                                        selectedCourse.description
                                    }
                                </p>

                                <div className="cayep-course-learning-meta">
                                    {selectedCourse.facilitator && (
                                        <span>
                                            <FaUserTie />

                                            {
                                                selectedCourse.facilitator
                                            }
                                        </span>
                                    )}

                                    {selectedCourse.level && (
                                        <span>
                                            <FaGraduationCap />

                                            {
                                                selectedCourse.level
                                            }
                                        </span>
                                    )}

                                    {selectedCourse.duration && (
                                        <span>
                                            <FaClock />

                                            {
                                                selectedCourse.duration
                                            }
                                        </span>
                                    )}

                                    {selectedVideo && (
                                        <span>
                                            <FaVideo />

                                            Video learning available
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="cayep-learning-header-mark">
                                <FaBookOpen />

                                <span>
                                    COURSE
                                </span>

                                <strong>
                                    LEARNING
                                </strong>
                            </div>
                        </div>

                        <div className="cayep-course-learning-layout">
                            <main className="cayep-course-learning-main">
                                <div className="cayep-course-video-panel">
                                    {selectedVideo ? (
                                        <>
                                            <div className="cayep-course-video-heading">
                                                <div>
                                                    <span>
                                                        TRAINING VIDEO
                                                    </span>

                                                    <h2>
                                                        Course Video
                                                    </h2>
                                                </div>

                                                <div className="cayep-video-indicator">
                                                    <FaCheckCircle />

                                                    Video Available
                                                </div>
                                            </div>

                                            <div className="cayep-course-video">
                                                {videoLoading && (
                                                    <div className="cayep-video-loading">
                                                        <FaSpinner />

                                                        <span>
                                                            Preparing course video...
                                                        </span>
                                                    </div>
                                                )}

                                                <video
                                                    controls
                                                    playsInline
                                                    preload="metadata"
                                                    poster={
                                                        selectedImage ||
                                                        undefined
                                                    }
                                                    onLoadStart={() =>
                                                        setVideoLoading(
                                                            true
                                                        )
                                                    }
                                                    onLoadedData={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                    onCanPlay={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                    onError={() =>
                                                        setVideoLoading(
                                                            false
                                                        )
                                                    }
                                                >
                                                    <source
                                                        src={
                                                            selectedVideo
                                                        }
                                                    />

                                                    Your browser does not
                                                    support the video player.
                                                </video>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="cayep-course-no-video">
                                            <div>
                                                <FaVideo />
                                            </div>

                                            <span>
                                                VIDEO CONTENT
                                            </span>

                                            <h2>
                                                No Course Video Available
                                            </h2>

                                            <p>
                                                Video learning
                                                content has not
                                                been added to
                                                this course yet.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="cayep-course-description-panel">
                                    <span>
                                        COURSE OVERVIEW
                                    </span>

                                    <h2>
                                        About This Training
                                    </h2>

                                    <p>
                                        {
                                            selectedCourse.description
                                        }
                                    </p>
                                </div>
                            </main>

                            <aside className="cayep-learning-sidebar">
                                <div className="cayep-learning-sidebar-card">
                                    {selectedImage ? (
                                        <div className="cayep-sidebar-thumbnail">
                                            <img
                                                src={
                                                    selectedImage
                                                }
                                                alt={
                                                    selectedCourse.course_title
                                                }
                                            />

                                            <div>
                                                <FaPlay />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="cayep-sidebar-thumbnail cayep-sidebar-no-image">
                                            <FaBookOpen />
                                        </div>
                                    )}

                                    <div className="cayep-sidebar-content">
                                        <span>
                                            COURSE
                                        </span>

                                        <h3>
                                            {
                                                selectedCourse.course_title
                                            }
                                        </h3>

                                        {selectedCourse.category && (
                                            <div className="cayep-sidebar-row">
                                                <FaLayerGroup />

                                                <span>
                                                    {
                                                        selectedCourse.category
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        {selectedCourse.facilitator && (
                                            <div className="cayep-sidebar-row">
                                                <FaUserTie />

                                                <span>
                                                    {
                                                        selectedCourse.facilitator
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        {selectedCourse.duration && (
                                            <div className="cayep-sidebar-row">
                                                <FaClock />

                                                <span>
                                                    {
                                                        selectedCourse.duration
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        {selectedCourse.level && (
                                            <div className="cayep-sidebar-row">
                                                <FaGraduationCap />

                                                <span>
                                                    {
                                                        selectedCourse.level
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        {selectedCourse.lessons && (
                                            <div className="cayep-sidebar-row">
                                                <FaLayerGroup />

                                                <span>
                                                    {
                                                        selectedCourse.lessons
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        <div className="cayep-sidebar-price">
                                            {selectedCourse.current_price !==
                                                null &&
                                            selectedCourse.current_price !==
                                                undefined &&
                                            selectedCourse.current_price !==
                                                ""
                                                ? `LRD ${formatMoney(
                                                      selectedCourse.current_price
                                                  )}`
                                                : "FREE"}
                                        </div>
                                    </div>
                                </div>
                            </aside>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <style>
                {courseStyles}
            </style>

            <section
                className="cayep-courses-section"
                ref={sectionRef}
            >
                <div className="cayep-courses-background">
                    <div className="cayep-grid" />

                    <div className="cayep-energy cayep-energy-one" />
                    <div className="cayep-energy cayep-energy-two" />
                    <div className="cayep-energy cayep-energy-three" />

                    <div className="cayep-orb cayep-orb-one" />
                    <div className="cayep-orb cayep-orb-two" />

                    <div className="cayep-particles">
                        {particles.map(
                            (
                                particle
                            ) => (
                                <span
                                    key={
                                        particle.id
                                    }
                                    style={{
                                        "--particle-x": `${particle.x}%`,
                                        "--particle-y": `${particle.y}%`,
                                        "--particle-delay": `${particle.delay}s`,
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>

                <div className="cayep-courses-container">
                    <div className="cayep-courses-intro">
                        <div>
                            <div className="cayep-section-label">
                                <span className="cayep-label-line" />

                                CAYEP • LEADERSHIP TRAINING
                            </div>

                            <h1 className="cayep-main-title">
                                DEVELOP

                                <span>
                                    LEADERS
                                </span>
                            </h1>

                            <p className="cayep-main-description">
                                Explore professional
                                training programs
                                designed to build
                                leadership capacity,
                                practical skills, and
                                organizational
                                excellence.
                            </p>

                            <div className="cayep-intro-actions">
                                <button
                                    type="button"
                                    className="cayep-primary-button"
                                    onClick={() => {
                                        document
                                            .getElementById(
                                                "cayep-course-catalog"
                                            )
                                            ?.scrollIntoView(
                                                {
                                                    behavior:
                                                        "smooth",
                                                }
                                            );
                                    }}
                                >
                                    <span>
                                        Explore Courses
                                    </span>

                                    <FaArrowRight />
                                </button>

                                <div className="cayep-learning-status">
                                    <span className="cayep-status-dot" />

                                    TRAINING PLATFORM ONLINE
                                </div>
                            </div>
                        </div>

                        <div className="cayep-training-visual">
                            <div className="cayep-visual-ring ring-one" />
                            <div className="cayep-visual-ring ring-two" />
                            <div className="cayep-visual-ring ring-three" />

                            <div className="cayep-visual-center">
                                <FaGraduationCap />

                                <span>
                                    CAYEP
                                </span>

                                <strong>
                                    LEADERSHIP
                                </strong>

                                <small>
                                    TRAINING INSTITUTE
                                </small>
                            </div>

                            <div className="cayep-floating-icon icon-one">
                                <FaBookOpen />
                            </div>

                            <div className="cayep-floating-icon icon-two">
                                <FaUsers />
                            </div>

                            <div className="cayep-floating-icon icon-three">
                                <FaVideo />
                            </div>
                        </div>
                    </div>

                    <div
                        className="cayep-section-heading"
                        id="cayep-course-catalog"
                    >
                        <div>
                            <span className="cayep-mini-label">
                                TRAINING CATALOG
                            </span>

                            <h3>
                                PROFESSIONAL

                                <span>
                                    DEVELOPMENT
                                </span>
                            </h3>
                        </div>

                        <p>
                            Select a training program
                            to view its course
                            information and access
                            available learning
                            content.
                        </p>
                    </div>

                    <div className="cayep-courses-grid">
                        {loading ? (
                            <div className="cayep-course-state">
                                <div className="cayep-state-icon cayep-loading-icon">
                                    <FaSpinner />
                                </div>

                                <h4>
                                    Loading Courses
                                </h4>

                                <p>
                                    Please wait while
                                    the available
                                    training programs
                                    are loaded.
                                </p>
                            </div>
                        ) : error ? (
                            <div className="cayep-course-state cayep-error-state">
                                <div className="cayep-state-icon">
                                    <FaExclamationTriangle />
                                </div>

                                <h4>
                                    Unable to Load
                                    Courses
                                </h4>

                                <p>
                                    {error}
                                </p>

                                <button
                                    type="button"
                                    className="cayep-course-button cayep-retry-button"
                                    onClick={() =>
                                        fetchCourses(
                                            true
                                        )
                                    }
                                >
                                    Try Again

                                    <FaArrowRight />
                                </button>
                            </div>
                        ) : courses.length ===
                          0 ? (
                            <div className="cayep-course-state">
                                <div className="cayep-state-icon">
                                    <FaBookOpen />
                                </div>

                                <h4>
                                    No Courses
                                    Available
                                </h4>

                                <p>
                                    There are
                                    currently no
                                    training courses
                                    available in
                                    the catalog.
                                </p>
                            </div>
                        ) : (
                            paginatedCourses.map(
                                (
                                    course,
                                    index
                                ) =>
                                    renderCourseCard(
                                        course,
                                        (currentPage -
                                            1) *
                                            COURSES_PER_PAGE +
                                            index
                                    )
                            )
                        )}
                    </div>

                    {!loading &&
                        !error &&
                        courses.length >
                            0 && (
                            <div
                                className={`cayep-course-pagination ${
                                    !hasPagination
                                        ? "cayep-pagination-single-page"
                                        : ""
                                }`}
                            >
                                <button
                                    type="button"
                                    className="cayep-pagination-button"
                                    disabled={
                                        !canGoPrevious
                                    }
                                    onClick={
                                        handlePrevious
                                    }
                                    aria-label="Go to previous courses"
                                    aria-disabled={
                                        !canGoPrevious
                                    }
                                >
                                    <FaArrowLeft />

                                    <span>
                                        Previous
                                    </span>
                                </button>

                                <div className="cayep-pagination-info">
                                    <strong>
                                        Page{" "}
                                        {
                                            currentPage
                                        }{" "}
                                        of{" "}
                                        {
                                            totalPages
                                        }
                                    </strong>

                                    <span>
                                        Showing{" "}
                                        {
                                            paginationStart
                                        }
                                        –
                                        {
                                            paginationEnd
                                        }{" "}
                                        of{" "}
                                        {
                                            courses.length
                                        }{" "}
                                        courses
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className="cayep-pagination-button"
                                    disabled={
                                        !canGoNext
                                    }
                                    onClick={
                                        handleNext
                                    }
                                    aria-label="Go to next courses"
                                    aria-disabled={
                                        !canGoNext
                                    }
                                >
                                    <span>
                                        Next
                                    </span>

                                    <FaArrowRight />
                                </button>
                            </div>
                        )}

                    {!loading &&
                        !error &&
                        courses.length >
                            0 && (
                            <div className="cayep-course-platform">
                                <div className="cayep-platform-glow" />

                                <div className="cayep-platform-icon">
                                    <FaGraduationCap />
                                </div>

                                <div className="cayep-platform-content">
                                    <span>
                                        CAYEP LEARNING ENVIRONMENT
                                    </span>

                                    <h3>
                                        Learn.

                                        <strong>
                                            Lead.
                                        </strong>

                                        Transform.
                                    </h3>

                                    <p>
                                        Open a course
                                        to access
                                        its learning
                                        environment
                                        and training
                                        video.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="cayep-platform-button"
                                    onClick={() => {
                                        document
                                            .getElementById(
                                                "cayep-course-catalog"
                                            )
                                            ?.scrollIntoView(
                                                {
                                                    behavior:
                                                        "smooth",
                                                }
                                            );
                                    }}
                                >
                                    Browse Training

                                    <FaArrowRight />
                                </button>
                            </div>
                        )}

                    <div className="cayep-course-footer">
                        <div>
                            <FaCheckCircle />

                            PROFESSIONAL TRAINING
                        </div>

                        <div>
                            <FaUsers />

                            LEADERSHIP DEVELOPMENT
                        </div>

                        <div>
                            <FaVideo />

                            DIGITAL LEARNING
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

const courseStyles = `
.cayep-courses-section {
    --navy: #062A4B;
    --deep-blue: #063A6B;
    --black: #0A0F12;
    --gold: #F6C944;
    --golden-yellow: #EFA81C;
    --orange: #C0750D;
    --green: #4FAE22;
    --olive-green: #5F6622;
    --sky-blue: #0F6DAB;
    --white: #F0EDEA;
    --light-gray: #C4C7C7;
    --antique-gold: #CBA454;
    --mouse-x: 50%;
    --mouse-y: 50%;
    position: relative;
    width: 100%;
    min-height: 100vh;
    overflow: hidden;
    padding: 105px 0 60px;
    background:
        radial-gradient(
            circle at var(--mouse-x) var(--mouse-y),
            rgba(246, 201, 68, 0.08),
            transparent 28%
        ),
        linear-gradient(
            135deg,
            #07131d 0%,
            #062A4B 46%,
            #081116 100%
        );
    color: var(--white);
    font-family:
        "Poppins",
        "Inter",
        Arial,
        sans-serif;
    isolation: isolate;
}

.cayep-courses-background {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    z-index: -1;
}

/* =========================================================
   CAYEP COURSE PAGINATION
========================================================= */

.cayep-course-pagination {
    width: 100%;
    max-width: 1250px;
    margin: 38px auto 0;

    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 20px;
    padding: 18px 20px;

    box-sizing: border-box;

    border: 1px solid rgba(246, 201, 68, 0.16);

    background:
        linear-gradient(
            145deg,
            rgba(240, 237, 234, 0.045),
            rgba(240, 237, 234, 0.018)
        );
}


/* =========================================================
   PAGINATION BUTTON
========================================================= */

.cayep-pagination-button {
    min-width: 125px;
    min-height: 45px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    gap: 9px;
    padding: 0 17px;

    border: 1px solid rgba(246, 201, 68, 0.45);

    background: rgba(6, 42, 75, 0.65);

    color: var(--gold);

    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.8px;

    cursor: pointer;

    transition:
        background 0.3s ease,
        color 0.3s ease,
        border-color 0.3s ease,
        transform 0.3s ease,
        box-shadow 0.3s ease;
}


/* =========================================================
   PAGINATION BUTTON — HOVER
========================================================= */

.cayep-pagination-button:hover:not(:disabled) {
    background: var(--gold);
    color: var(--black);

    border-color: var(--gold);

    transform: translateY(-2px);

    box-shadow:
        0 10px 25px rgba(246, 201, 68, 0.14);
}


/* =========================================================
   PAGINATION ARROW ANIMATION
========================================================= */

.cayep-pagination-button:first-child:hover:not(:disabled) svg {
    transform: translateX(-4px);
}

.cayep-pagination-button:last-child:hover:not(:disabled) svg {
    transform: translateX(4px);
}

.cayep-pagination-button svg {
    transition:
        transform 0.3s ease;
}


/* =========================================================
   PAGINATION BUTTON — DISABLED
========================================================= */

.cayep-pagination-button:disabled {
    opacity: 0.3;

    cursor: not-allowed;

    border-color: rgba(240, 237, 234, 0.12);

    color: #687278;

    background: rgba(240, 237, 234, 0.025);
}


/* =========================================================
   PAGINATION INFORMATION
========================================================= */

.cayep-pagination-info {
    min-width: 220px;

    display: flex;
    flex-direction: column;

    align-items: center;
    justify-content: center;

    text-align: center;
}


/* =========================================================
   CURRENT PAGE / PAGE COUNT
========================================================= */

.cayep-pagination-info strong {
    color: var(--white);

    font-size: 12px;
    font-weight: 800;

    letter-spacing: 1px;
}


/* =========================================================
   PAGINATION DESCRIPTION
========================================================= */

.cayep-pagination-info span {
    margin-top: 5px;

    color: #7f8a90;

    font-size: 9px;
    font-weight: 600;

    letter-spacing: 0.4px;
}


/* =========================================================
   TABLET / MOBILE
========================================================= */

@media (max-width: 650px) {

    .cayep-course-pagination {
        flex-direction: column;

        gap: 14px;
        padding: 18px;
    }

    .cayep-pagination-button {
        width: 100%;
    }

    .cayep-pagination-info {
        order: -1;
        width: 100%;
    }
}


/* =========================================================
   SMALL MOBILE
========================================================= */

@media (max-width: 420px) {

    .cayep-course-pagination {
        margin-top: 28px;
        padding: 15px;
    }

    .cayep-pagination-info strong {
        font-size: 11px;
    }

    .cayep-pagination-info span {
        font-size: 8px;
    }
}

.cayep-grid {
    position: absolute;
    inset: -20%;
    opacity: 0.16;
    background-image:
        linear-gradient(
            rgba(240, 237, 234, 0.035) 1px,
            transparent 1px
        ),
        linear-gradient(
            90deg,
            rgba(240, 237, 234, 0.035) 1px,
            transparent 1px
        );
    background-size: 65px 65px;
    transform:
        perspective(700px)
        rotateX(58deg)
        translateY(20%);
    transform-origin: center bottom;
    animation:
        cayepGridMove
        20s
        linear
        infinite;
}

@keyframes cayepGridMove {
    0% {
        transform:
            perspective(700px)
            rotateX(58deg)
            translateY(10%);
    }

    100% {
        transform:
            perspective(700px)
            rotateX(58deg)
            translateY(17%);
    }
}

.cayep-energy {
    position: absolute;
    width: 620px;
    height: 2px;
    background:
        linear-gradient(
            90deg,
            transparent,
            var(--gold),
            #ffffff,
            var(--golden-yellow),
            transparent
        );
    opacity: 0.25;
    filter: blur(1px);
    box-shadow:
        0 0 14px rgba(246, 201, 68, 0.6),
        0 0 30px rgba(246, 201, 68, 0.25);
}

.cayep-energy-one {
    top: 18%;
    left: -700px;
    transform: rotate(-17deg);
    animation:
        cayepEnergyOne
        9s
        linear
        infinite;
}

.cayep-energy-two {
    top: 64%;
    left: -700px;
    transform: rotate(13deg);
    animation:
        cayepEnergyTwo
        11s
        linear
        infinite
        2s;
}

.cayep-energy-three {
    top: 84%;
    left: -700px;
    transform: rotate(-8deg);
    animation:
        cayepEnergyThree
        13s
        linear
        infinite
        1s;
}

@keyframes cayepEnergyOne {
    0% {
        left: -700px;
    }

    100% {
        left: 110%;
    }
}

@keyframes cayepEnergyTwo {
    0% {
        left: -800px;
    }

    100% {
        left: 120%;
    }
}

@keyframes cayepEnergyThree {
    0% {
        left: -900px;
    }

    100% {
        left: 125%;
    }
}

.cayep-orb {
    position: absolute;
    border-radius: 50%;
    background: var(--gold);
    filter: blur(110px);
    opacity: 0.07;
    animation:
        cayepOrbFloat
        9s
        ease-in-out
        infinite;
}

.cayep-orb-one {
    width: 460px;
    height: 460px;
    top: -180px;
    right: -150px;
}

.cayep-orb-two {
    width: 380px;
    height: 380px;
    bottom: 12%;
    left: -180px;
    animation-delay: 2s;
}

@keyframes cayepOrbFloat {
    0%,
    100% {
        transform:
            translate3d(0, 0, 0)
            scale(1);
    }

    50% {
        transform:
            translate3d(30px, -25px, 0)
            scale(1.12);
    }
}

.cayep-particles {
    position: absolute;
    inset: 0;
}

.cayep-particles span {
    position: absolute;
    left: var(--particle-x);
    top: var(--particle-y);
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: var(--gold);
    box-shadow:
        0 0 8px var(--gold),
        0 0 15px rgba(246, 201, 68, 0.45);
    opacity: 0;
    animation:
        cayepParticleFloat
        5s
        ease-in-out
        infinite;
    animation-delay:
        var(--particle-delay);
}

@keyframes cayepParticleFloat {
    0% {
        opacity: 0;
        transform:
            translate3d(0, 25px, 0)
            scale(0.5);
    }

    30% {
        opacity: 0.65;
    }

    70% {
        opacity: 0.3;
    }

    100% {
        opacity: 0;
        transform:
            translate3d(20px, -70px, 0)
            scale(1.2);
    }
}

.cayep-courses-container {
    position: relative;
    width: min(1380px, 92%);
    margin: 0 auto;
    z-index: 2;
}

.cayep-courses-intro {
    display: grid;
    grid-template-columns:
        minmax(0, 1.05fr)
        minmax(400px, 0.95fr);
    gap: 80px;
    align-items: center;
    margin-bottom: 115px;
}

.cayep-section-label {
    display: flex;
    align-items: center;
    gap: 13px;
    color: var(--gold);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 4px;
    margin-bottom: 22px;
}

.cayep-label-line {
    width: 38px;
    height: 1px;
    background: var(--gold);
    box-shadow:
        0 0 10px rgba(246, 201, 68, 0.65);
}

.cayep-main-title {
    margin: 0;
    max-width: 800px;
    font-size: clamp(55px, 7vw, 105px);
    line-height: 0.94;
    font-weight: 800;
    letter-spacing: -5px;
}

.cayep-main-title span {
    display: block;
    color: transparent;
    -webkit-text-stroke:
        1px rgba(246, 201, 68, 0.75);
}

.cayep-main-description {
    max-width: 650px;
    margin: 35px 0 0;
    color: #c4c7c7;
    font-size: 16px;
    line-height: 1.9;
}

.cayep-intro-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 30px;
    margin-top: 40px;
}

.cayep-primary-button,
.cayep-platform-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    min-height: 54px;
    padding: 0 27px;
    border: 1px solid var(--gold);
    background: var(--gold);
    color: var(--black);
    font-family: inherit;
    text-decoration: none;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.4px;
    cursor: pointer;
    transition:
        transform 0.3s ease,
        box-shadow 0.3s ease,
        background 0.3s ease;
}

.cayep-primary-button:hover,
.cayep-platform-button:hover {
    transform: translateY(-4px);
    background: var(--golden-yellow);
    box-shadow:
        0 12px 35px
        rgba(246, 201, 68, 0.22);
}

.cayep-primary-button svg,
.cayep-platform-button svg {
    transition:
        transform 0.3s ease;
}

.cayep-primary-button:hover svg,
.cayep-platform-button:hover svg {
    transform: translateX(5px);
}

.cayep-learning-status {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #9da6ab;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 2px;
}

.cayep-status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--green);
    box-shadow:
        0 0 0 5px rgba(79, 174, 34, 0.08),
        0 0 15px rgba(79, 174, 34, 0.8);
    animation:
        cayepStatusPulse
        1.8s
        infinite;
}

@keyframes cayepStatusPulse {
    0%,
    100% {
        box-shadow:
            0 0 0 5px rgba(79, 174, 34, 0.08),
            0 0 15px rgba(79, 174, 34, 0.8);
    }

    50% {
        box-shadow:
            0 0 0 11px rgba(79, 174, 34, 0.02),
            0 0 25px rgba(79, 174, 34, 0.8);
    }
}

.cayep-training-visual {
    position: relative;
    width: min(100%, 520px);
    aspect-ratio: 1 / 1;
    margin: 0 auto;
    display: grid;
    place-items: center;
}

.cayep-visual-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(246, 201, 68, 0.2);
    animation:
        cayepVisualRing
        14s
        linear
        infinite;
}

.ring-one {
    width: 92%;
    height: 92%;
}

.ring-two {
    width: 73%;
    height: 73%;
    border-style: dashed;
    border-color: rgba(15, 109, 171, 0.35);
    animation-duration: 18s;
    animation-direction: reverse;
}

.ring-three {
    width: 52%;
    height: 52%;
    border-color: rgba(246, 201, 68, 0.35);
    animation-duration: 22s;
}

@keyframes cayepVisualRing {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.cayep-visual-center {
    position: relative;
    width: 48%;
    height: 48%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border:
        1px solid rgba(246, 201, 68, 0.4);
    background:
        linear-gradient(
            145deg,
            rgba(6, 58, 107, 0.96),
            rgba(10, 15, 18, 0.96)
        );
    box-shadow:
        0 25px 70px rgba(0, 0, 0, 0.35),
        inset 0 0 40px rgba(246, 201, 68, 0.04);
    transform: rotate(-4deg);
    z-index: 3;
}

.cayep-visual-center svg {
    margin-bottom: 8px;
    color: var(--gold);
    font-size: 38px;
}

.cayep-visual-center span {
    color: var(--gold);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 4px;
}

.cayep-visual-center strong {
    margin-top: 4px;
    color: var(--white);
    font-size: 18px;
    letter-spacing: 2px;
}

.cayep-visual-center small {
    margin-top: 3px;
    color: var(--light-gray);
    font-size: 8px;
    letter-spacing: 3px;
}

.cayep-floating-icon {
    position: absolute;
    width: 58px;
    height: 58px;
    display: grid;
    place-items: center;
    border:
        1px solid rgba(246, 201, 68, 0.4);
    background:
        rgba(6, 42, 75, 0.9);
    color: var(--gold);
    box-shadow:
        0 0 25px rgba(246, 201, 68, 0.08);
    z-index: 5;
    animation:
        cayepFloatingIcon
        4s
        ease-in-out
        infinite;
}

.cayep-floating-icon svg {
    font-size: 20px;
}

.icon-one {
    top: 8%;
    left: 5%;
}

.icon-two {
    top: 18%;
    right: 3%;
    animation-delay: 0.8s;
}

.icon-three {
    bottom: 9%;
    right: 10%;
    animation-delay: 1.5s;
}

@keyframes cayepFloatingIcon {
    0%,
    100% {
        transform:
            translateY(0)
            rotate(0deg);
    }

    50% {
        transform:
            translateY(-12px)
            rotate(5deg);
    }
}

.cayep-section-heading {
    display: grid;
    grid-template-columns:
        minmax(0, 1fr)
        minmax(300px, 0.75fr);
    gap: 70px;
    align-items: end;
    margin-bottom: 45px;
    padding-bottom: 28px;
    border-bottom:
        1px solid rgba(240, 237, 234, 0.1);
}

.cayep-mini-label {
    color: var(--gold);
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-section-heading h3 {
    margin: 10px 0 0;
    font-size: clamp(34px, 4vw, 57px);
    line-height: 1.05;
    letter-spacing: -2px;
}

.cayep-section-heading h3 span {
    display: block;
    color: #8d989e;
}

.cayep-section-heading p {
    margin: 0;
    color: #aab2b6;
    font-size: 14px;
    line-height: 1.9;
}

.cayep-courses-grid {
    width: 100%;
    max-width: 1250px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    gap: 22px;
    box-sizing: border-box;
}

.cayep-courses-grid .cayep-course-card {
    flex: 0 1 385px;
    width: 385px;
    max-width: 100%;
    box-sizing: border-box;
}

.cayep-course-card {
    position: relative;
    overflow: hidden;
    border:
        1px solid rgba(240, 237, 234, 0.11);
    background:
        linear-gradient(
            145deg,
            rgba(240, 237, 234, 0.075),
            rgba(240, 237, 234, 0.025)
        );
    box-shadow:
        0 15px 45px rgba(0, 0, 0, 0.16);
    transition:
        transform 0.4s ease,
        border-color 0.4s ease,
        box-shadow 0.4s ease;
    cursor: pointer;
}

.cayep-course-card:hover {
    transform: translateY(-9px);
    border-color:
        rgba(246, 201, 68, 0.5);
    box-shadow:
        0 25px 60px rgba(0, 0, 0, 0.3);
}

.cayep-course-image {
    position: relative;
    height: 225px;
    overflow: hidden;
    background: var(--deep-blue);
}

.cayep-course-image img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    transition:
        transform 0.7s ease;
}

.cayep-course-card:hover
    .cayep-course-image img {
    transform: scale(1.08);
}

.cayep-course-image-overlay {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(
            180deg,
            rgba(6, 42, 75, 0.08),
            rgba(6, 42, 75, 0.9)
        );
    pointer-events: none;
}

.cayep-course-no-image {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--gold);
    background:
        linear-gradient(
            135deg,
            #063A6B,
            #07131d
        );
}

.cayep-course-no-image svg {
    font-size: 42px;
    opacity: 0.7;
}

.cayep-course-no-image span {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-course-number {
    position: absolute;
    top: 16px;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border:
        1px solid rgba(246, 201, 68, 0.7);
    background:
        rgba(6, 42, 75, 0.82);
    color: var(--gold);
    font-size: 11px;
    font-weight: 800;
    z-index: 3;
}

.cayep-course-category {
    position: absolute;
    left: 17px;
    bottom: 16px;
    max-width: calc(100% - 75px);
    padding: 7px 10px;
    background: var(--gold);
    color: var(--black);
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    z-index: 3;
}

.cayep-course-play {
    position: absolute;
    right: 17px;
    bottom: 16px;
    width: 43px;
    height: 43px;
    display: grid;
    place-items: center;
    border:
        1px solid rgba(246, 201, 68, 0.8);
    border-radius: 50%;
    background:
        rgba(6, 42, 75, 0.88);
    color: var(--gold);
    cursor: pointer;
    z-index: 4;
    transition:
        transform 0.3s ease,
        background 0.3s ease,
        color 0.3s ease;
}

.cayep-course-play:hover {
    transform: scale(1.1);
    background: var(--gold);
    color: var(--black);
}

.cayep-course-play svg {
    font-size: 14px;
    margin-left: 2px;
}

.cayep-course-content {
    padding: 25px;
}

.cayep-course-content h4 {
    margin: 0 0 11px;
    color: var(--white);
    font-size: 20px;
    line-height: 1.3;
}

.cayep-course-description {
    min-height: 78px;
    margin: 0;
    color: #9da6ab;
    font-size: 12px;
    line-height: 1.75;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.cayep-course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 9px 15px;
    margin-top: 20px;
}

.cayep-course-meta span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #aeb5b8;
    font-size: 9px;
    font-weight: 700;
}

.cayep-course-meta svg {
    color: var(--gold);
    font-size: 11px;
}

.cayep-course-capacity {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 20px;
    padding: 12px;
    border:
        1px solid rgba(240, 237, 234, 0.09);
    background:
        rgba(6, 42, 75, 0.45);
}

.cayep-capacity-main {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    min-width: 0;
}

.cayep-capacity-main > svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: var(--gold);
    font-size: 14px;
}

.cayep-capacity-main strong {
    display: block;
    color: var(--white);
    font-size: 10px;
    line-height: 1.3;
}

.cayep-capacity-main span {
    display: block;
    margin-top: 3px;
    color: #7f8a90;
    font-size: 8px;
}

.cayep-capacity-main small {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    color: var(--sky-blue);
    font-size: 8px;
    font-weight: 700;
}

.cayep-capacity-main small svg {
    color: var(--orange);
    font-size: 8px;
}

.cayep-slot-badge {
    flex-shrink: 0;
    padding: 5px 8px;
    border:
        1px solid rgba(79, 174, 34, 0.4);
    background:
        rgba(79, 174, 34, 0.08);
    color: #7bd45c;
    font-size: 8px;
    font-weight: 800;
    white-space: nowrap;
}

.cayep-slot-badge.last {
    border-color:
        rgba(246, 201, 68, 0.4);
    background:
        rgba(246, 201, 68, 0.08);
    color: var(--gold);
}

.cayep-slot-badge.full {
    border-color:
        rgba(255, 120, 120, 0.4);
    background:
        rgba(255, 80, 80, 0.08);
    color: #ff8b8b;
}

.cayep-course-divider {
    width: 100%;
    height: 1px;
    margin: 21px 0 18px;
    background:
        rgba(240, 237, 234, 0.08);
}

.cayep-course-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
}

.cayep-course-price {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.cayep-course-price strong {
    color: var(--gold);
    font-size: 23px;
    line-height: 1;
}

.cayep-course-price del {
    color: #687278;
    font-size: 11px;
}

.cayep-course-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 39px;
    padding: 0 13px;
    border:
        1px solid rgba(246, 201, 68, 0.65);
    background: transparent;
    color: var(--gold);
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
    transition:
        background 0.3s ease,
        color 0.3s ease,
        transform 0.3s ease;
}

.cayep-course-button:hover {
    background: var(--gold);
    color: var(--black);
    transform: translateY(-2px);
}

.cayep-course-button svg {
    transition:
        transform 0.3s ease;
}

.cayep-course-button:hover svg {
    transform: translateX(4px);
}

.cayep-course-state {
    width: 100%;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 45px 25px;
    box-sizing: border-box;
    border:
        1px solid rgba(240, 237, 234, 0.1);
    background:
        rgba(240, 237, 234, 0.035);
    text-align: center;
    flex: 1 1 100%;
}

.cayep-state-icon {
    width: 65px;
    height: 65px;
    display: grid;
    place-items: center;
    margin-bottom: 20px;
    border:
        1px solid rgba(246, 201, 68, 0.4);
    color: var(--gold);
    font-size: 23px;
}

.cayep-loading-icon svg {
    animation:
        cayepSpinner
        1s
        linear
        infinite;
}

@keyframes cayepSpinner {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.cayep-course-state h4 {
    margin: 0 0 9px;
    color: var(--white);
    font-size: 22px;
}

.cayep-course-state p {
    max-width: 500px;
    margin: 0;
    color: #909a9f;
    font-size: 13px;
    line-height: 1.8;
}

.cayep-error-state .cayep-state-icon {
    border-color:
        rgba(255, 120, 120, 0.4);
    color: #ff8b8b;
}

.cayep-retry-button {
    margin-top: 20px;
}

.cayep-course-platform {
    position: relative;
    display: grid;
    grid-template-columns:
        auto 1fr auto;
    align-items: center;
    gap: 30px;
    margin-top: 70px;
    padding: 38px 45px;
    overflow: hidden;
    border:
        1px solid rgba(246, 201, 68, 0.28);
    background:
        linear-gradient(
            110deg,
            rgba(246, 201, 68, 0.09),
            rgba(15, 109, 171, 0.08),
            rgba(240, 237, 234, 0.025)
        );
}

.cayep-platform-glow {
    position: absolute;
    width: 350px;
    height: 350px;
    right: 8%;
    top: -250px;
    border-radius: 50%;
    background: var(--gold);
    filter: blur(120px);
    opacity: 0.08;
    animation:
        cayepPlatformGlow
        5s
        ease-in-out
        infinite;
}

@keyframes cayepPlatformGlow {
    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.35);
    }
}

.cayep-platform-icon {
    position: relative;
    width: 65px;
    height: 65px;
    display: grid;
    place-items: center;
    border:
        1px solid var(--gold);
    color: var(--gold);
    font-size: 23px;
}

.cayep-platform-content {
    position: relative;
}

.cayep-platform-content > span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-platform-content h3 {
    margin: 5px 0;
    color: var(--white);
    font-size: 25px;
}

.cayep-platform-content h3 strong {
    color: var(--gold);
}

.cayep-platform-content p {
    max-width: 720px;
    margin: 0;
    color: #909a9f;
    font-size: 12px;
    line-height: 1.7;
}

.cayep-course-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 50px;
    margin-top: 45px;
    color: #687278;
}

.cayep-course-footer > div {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 2px;
}

.cayep-course-footer svg {
    color: var(--gold);
    font-size: 13px;
}

.cayep-learning-page {
    min-height: 100vh;
}

.cayep-course-learning-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 55px;
}

.cayep-back-courses-button {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 43px;
    padding: 0 16px;
    border:
        1px solid rgba(246, 201, 68, 0.45);
    background:
        rgba(6, 42, 75, 0.6);
    color: var(--gold);
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.5px;
    transition:
        background 0.3s ease,
        transform 0.3s ease;
}

.cayep-back-courses-button:hover {
    background: var(--gold);
    color: var(--black);
    transform: translateX(-3px);
}

.cayep-course-learning-header {
    display: grid;
    grid-template-columns:
        minmax(0, 1fr)
        260px;
    gap: 50px;
    align-items: center;
    margin-bottom: 55px;
}

.cayep-course-learning-header h1 {
    max-width: 850px;
    margin: 0;
    color: var(--white);
    font-size: clamp(38px, 5vw, 70px);
    line-height: 1;
    letter-spacing: -3px;
}

.cayep-course-learning-header p {
    max-width: 760px;
    margin: 25px 0 0;
    color: #9da6ab;
    font-size: 14px;
    line-height: 1.9;
}

.cayep-course-learning-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 15px 25px;
    margin-top: 25px;
}

.cayep-course-learning-meta span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #aeb5b8;
    font-size: 10px;
    font-weight: 700;
}

.cayep-course-learning-meta svg {
    color: var(--gold);
}

.cayep-learning-header-mark {
    width: 220px;
    height: 220px;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border:
        1px solid rgba(246, 201, 68, 0.35);
    background:
        linear-gradient(
            145deg,
            rgba(6, 58, 107, 0.75),
            rgba(10, 15, 18, 0.9)
        );
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.3);
    transform: rotate(3deg);
}

.cayep-learning-header-mark svg {
    margin-bottom: 12px;
    color: var(--gold);
    font-size: 42px;
}

.cayep-learning-header-mark span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 4px;
}

.cayep-learning-header-mark strong {
    margin-top: 4px;
    color: var(--white);
    font-size: 17px;
    letter-spacing: 3px;
}

.cayep-course-learning-layout {
    display: grid;
    grid-template-columns:
        minmax(0, 1fr)
        330px;
    gap: 28px;
    align-items: start;
}

.cayep-course-learning-main {
    min-width: 0;
}

.cayep-course-video-panel,
.cayep-course-description-panel {
    border:
        1px solid rgba(240, 237, 234, 0.1);
    background:
        linear-gradient(
            145deg,
            rgba(240, 237, 234, 0.055),
            rgba(240, 237, 234, 0.02)
        );
    box-shadow:
        0 18px 50px rgba(0, 0, 0, 0.2);
}

.cayep-course-video-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 22px 25px;
    border-bottom:
        1px solid rgba(240, 237, 234, 0.08);
}

.cayep-course-video-heading span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-course-video-heading h2 {
    margin: 5px 0 0;
    color: var(--white);
    font-size: 22px;
}

.cayep-video-indicator {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #7bd45c;
    font-size: 9px;
    font-weight: 800;
    white-space: nowrap;
}

.cayep-course-video {
    position: relative;
    min-height: 420px;
    background: #03070a;
}

.cayep-course-video video {
    width: 100%;
    height: auto;
    min-height: 420px;
    display: block;
    background: #03070a;
    object-fit: contain;
}

.cayep-video-loading {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    z-index: 2;
    background:
        rgba(3, 7, 10, 0.88);
    color: var(--gold);
    font-size: 11px;
    font-weight: 700;
}

.cayep-video-loading svg {
    font-size: 25px;
    animation:
        cayepSpinner
        0.9s
        linear
        infinite;
}

.cayep-course-no-video {
    min-height: 420px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 45px;
    text-align: center;
}

.cayep-course-no-video > div {
    width: 70px;
    height: 70px;
    display: grid;
    place-items: center;
    margin-bottom: 20px;
    border:
        1px solid rgba(246, 201, 68, 0.3);
    color: var(--gold);
    font-size: 25px;
}

.cayep-course-no-video > span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-course-no-video h2 {
    margin: 10px 0;
    color: var(--white);
    font-size: 24px;
}

.cayep-course-no-video p {
    max-width: 480px;
    margin: 0;
    color: #909a9f;
    font-size: 12px;
    line-height: 1.8;
}

.cayep-course-description-panel {
    margin-top: 25px;
    padding: 30px;
}

.cayep-course-description-panel > span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-course-description-panel h2 {
    margin: 8px 0 12px;
    color: var(--white);
    font-size: 27px;
}

.cayep-course-description-panel p {
    max-width: 850px;
    margin: 0;
    color: #9da6ab;
    font-size: 13px;
    line-height: 1.9;
}

.cayep-learning-sidebar {
    min-width: 0;
}

.cayep-learning-sidebar-card {
    position: sticky;
    top: 25px;
    overflow: hidden;
    border:
        1px solid rgba(246, 201, 68, 0.18);
    background:
        linear-gradient(
            145deg,
            rgba(6, 42, 75, 0.85),
            rgba(10, 15, 18, 0.92)
        );
    box-shadow:
        0 20px 55px rgba(0, 0, 0, 0.3);
}

.cayep-sidebar-thumbnail {
    position: relative;
    height: 210px;
    overflow: hidden;
    background: var(--deep-blue);
}

.cayep-sidebar-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.cayep-sidebar-thumbnail > div {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background:
        rgba(6, 42, 75, 0.35);
    color: var(--gold);
    font-size: 35px;
}

.cayep-sidebar-no-image {
    display: grid;
    place-items: center;
    color: var(--gold);
    font-size: 40px;
    background:
        linear-gradient(
            135deg,
            #063A6B,
            #07131d
        );
}

.cayep-sidebar-content {
    padding: 24px;
}

.cayep-sidebar-content > span {
    color: var(--gold);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 3px;
}

.cayep-sidebar-content h3 {
    margin: 8px 0 20px;
    color: var(--white);
    font-size: 22px;
    line-height: 1.35;
}

.cayep-sidebar-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 0;
    border-top:
        1px solid rgba(240, 237, 234, 0.07);
    color: #aeb5b8;
    font-size: 10px;
}

.cayep-sidebar-row svg {
    flex-shrink: 0;
    color: var(--gold);
}

.cayep-sidebar-price {
    margin-top: 20px;
    padding-top: 18px;
    border-top:
        1px solid rgba(240, 237, 234, 0.09);
    color: var(--gold);
    font-size: 25px;
    font-weight: 900;
}

.cayep-pagination-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    border-color: rgba(240, 237, 234, 0.12);
    color: #687278;
    background: rgba(240, 237, 234, 0.025);
}

@media (max-width: 1200px) {
    .cayep-courses-intro {
        gap: 40px;
    }

    .cayep-courses-grid {
        max-width: 850px;
        gap: 22px;
    }

    .cayep-courses-grid .cayep-course-card {
        flex: 0 1 400px;
        width: 400px;
    }

    .cayep-course-learning-layout {
        grid-template-columns:
            minmax(0, 1fr)
            290px;
    }
}

@media (max-width: 900px) {
    .cayep-courses-section {
        padding-top: 80px;
    }

    .cayep-courses-intro {
        grid-template-columns: 1fr;
        margin-bottom: 90px;
    }

    .cayep-main-title {
        font-size: clamp(
            55px,
            12vw,
            85px
        );
    }

    .cayep-training-visual {
        max-width: 500px;
        margin-top: 10px;
    }

    .cayep-section-heading {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .cayep-course-platform {
        grid-template-columns:
            auto 1fr;
    }

    .cayep-platform-button {
        grid-column: 1 / -1;
        width: max-content;
    }

    .cayep-course-learning-header {
        grid-template-columns: 1fr;
    }

    .cayep-learning-header-mark {
        width: 160px;
        height: 160px;
        margin-left: 0;
    }

    .cayep-course-learning-layout {
        grid-template-columns: 1fr;
    }

    .cayep-learning-sidebar {
        order: -1;
    }

    .cayep-learning-sidebar-card {
        position: relative;
        top: auto;
    }
}

@media (max-width: 850px) {
    .cayep-courses-grid {
        width: 100%;
        max-width: 620px;
        gap: 20px;
    }

    .cayep-courses-grid .cayep-course-card {
        flex: 0 1 100%;
        width: 100%;
    }
}

@media (max-width: 650px) {
    .cayep-courses-section {
        padding:
            70px 0
            35px;
    }

    .cayep-courses-container {
        width: 90%;
    }

    .cayep-main-title {
        font-size: 52px;
        letter-spacing: -3px;
    }

    .cayep-main-description {
        font-size: 14px;
    }

    .cayep-intro-actions {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-courses-grid {
        width: 100%;
        max-width: 100%;
        gap: 18px;
    }

    .cayep-courses-grid .cayep-course-card {
        flex: 1 1 100%;
        width: 100%;
    }

    .cayep-course-image {
        height: 230px;
    }

    .cayep-course-platform {
        grid-template-columns: 1fr;
        padding: 30px;
    }

    .cayep-platform-button {
        width: 100%;
    }

    .cayep-course-bottom {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-course-button {
        width: 100%;
        justify-content: center;
    }

    .cayep-floating-icon {
        width: 46px;
        height: 46px;
    }

    .cayep-floating-icon svg {
        font-size: 15px;
    }

    .cayep-course-learning-header h1 {
        font-size: 36px;
    }

    .cayep-course-learning-meta {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-course-video {
        min-height: 220px;
    }

    .cayep-course-video video {
        min-height: 220px;
    }

    .cayep-course-video-heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-course-learning-top {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-back-courses-button {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 420px) {
    .cayep-main-title {
        font-size: 45px;
    }

    .cayep-section-heading h3 {
        font-size: 32px;
    }

    .cayep-course-content {
        padding: 22px;
    }

    .cayep-course-capacity {
        align-items: flex-start;
        flex-direction: column;
    }

    .cayep-course-button {
        width: 100%;
        justify-content: center;
    }

    .cayep-visual-center {
        width: 52%;
        height: 52%;
    }

    .cayep-visual-center strong {
        font-size: 14px;
    }

    .cayep-course-learning-header h1 {
        font-size: 31px;
    }

    .cayep-course-description-panel {
        padding: 17px;
    }

    .cayep-sidebar-content {
        padding: 19px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .cayep-courses-section *,
    .cayep-courses-section *::before,
    .cayep-courses-section *::after {
        animation-duration:
            0.01ms !important;
        animation-iteration-count:
            1 !important;
        scroll-behavior:
            auto !important;
    }
}
`;

export default Card;